import type {
  MatchEventCategory,
  MatchEventFrame,
  MatchEventsFilter,
} from '../types/matchEvents.types';
import { DEFAULT_MATCH_EVENT_CATEGORIES } from '../types/matchEvents.types';
import {
  getCategoryDisplay,
  type CategoryDisplayConfig,
} from '../components/matchEvents/category-display';

/**
 * Hard ceiling on how many raw frames the tab keeps in memory for one
 * fixture. The feed is a live, top-anchored view; without a cap the
 * buffer would grow unbounded over a 90-minute match. Older frames fall
 * off the bottom (a reconnect's `lastStreamId` replay re-seeds from the
 * cursor, and the server trims the stream anyway).
 */
export const MAX_MATCH_EVENT_ROWS = 1000;

/**
 * Game clock seconds → `mm'ss` (e.g. 125 → `2'05`, 5 → `0'05`). Null /
 * negative → `—`. The apostrophe form mirrors how live-score tickers
 * render match time.
 */
export function secondsToClock(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}'${String(secs).padStart(2, '0')}`;
}

/** Confidence 0..1 → integer percent (clamped 0..100). */
export function confidencePct(confidence: number | null | undefined): number {
  if (confidence == null || !Number.isFinite(confidence)) return 0;
  return Math.max(0, Math.min(100, Math.round(confidence * 100)));
}

/**
 * The home:away value string for the value column. Prefers the split
 * `homeValue`/`awayValue` positions; falls back to the single `value`.
 */
export function formatEventValue(frame: MatchEventFrame): string | null {
  if (frame.homeValue != null || frame.awayValue != null) {
    return `${frame.homeValue ?? '—'}:${frame.awayValue ?? '—'}`;
  }
  return frame.value;
}

/** One half's home:away reading for a metric. */
export interface HalfTally {
  periodId: number;
  periodName: string;
  home: string;
  away: string;
}

/**
 * A metric (goals / corners) broken down by half PLUS the match total.
 * LSports resets the accumulated count PER HALF, so the match total is the sum
 * of each half's latest reading — showing the current-half value alone would
 * under-count in the 2nd half.
 */
export interface MetricSummary {
  byHalf: HalfTally[];
  total: { home: number; away: number };
}

/** The match scoreboard derived from the live frames. */
export interface MatchSummary {
  goals: MetricSummary | null;
  corners: MetricSummary | null;
}

/**
 * Derive the goals + corners tally per HALF and the match total. For each
 * (category, period) we keep the highest-confidence reading (ties → the latest
 * in arrival order — follows increments + VAR reverts, ignores low-confidence
 * in-progress estimates), order the halves by period id, and sum them for the
 * match total (LSports accumulates per half, not across the match).
 */
export function deriveMatchSummary(frames: MatchEventFrame[]): MatchSummary {
  const build = (category: MatchEventCategory): MetricSummary | null => {
    const bestPerPeriod = new Map<number, MatchEventFrame>();
    for (const f of frames) {
      if (f.category !== category) continue;
      if (f.homeValue == null || f.awayValue == null || f.period == null) continue;
      const prev = bestPerPeriod.get(f.period.id);
      if (!prev || f.confidence >= prev.confidence) bestPerPeriod.set(f.period.id, f);
    }
    if (bestPerPeriod.size === 0) return null;

    const byHalf: HalfTally[] = [...bestPerPeriod.values()]
      .sort((a, b) => (a.period as { id: number }).id - (b.period as { id: number }).id)
      .map((f) => ({
        periodId: (f.period as { id: number }).id,
        periodName: (f.period as { name: string }).name,
        home: f.homeValue as string,
        away: f.awayValue as string,
      }));

    const total = byHalf.reduce(
      (acc, h) => ({
        home: acc.home + (Number(h.home) || 0),
        away: acc.away + (Number(h.away) || 0),
      }),
      { home: 0, away: 0 },
    );
    return { byHalf, total };
  };
  return { goals: build('goal'), corners: build('corner') };
}

/**
 * A flattened, display-ready view model for one match-event row. Pure
 * derivation from a `MatchEventFrame` — the component renders it and
 * owns nothing but layout. Keeping this pure makes the seconds→clock,
 * category→display and value collapse trivially unit-testable.
 */
export interface MatchEventRow {
  frame: MatchEventFrame;
  display: CategoryDisplayConfig;
  category: MatchEventCategory;
  name: string;
  team: 'home' | 'away' | null;
  playerName: string | null;
  value: string | null;
  clock: string;
  periodName: string | null;
  confidencePct: number;
  receivedAt: string;
  /** Stable dedupe/collapse key (incidentKey, falling back to streamId). */
  collapseKey: string;
}

/** Build the display-ready row view model from a raw frame. */
export function frameToRow(frame: MatchEventFrame): MatchEventRow {
  return {
    frame,
    display: getCategoryDisplay(frame.category),
    category: frame.category,
    name: frame.name,
    team: frame.team,
    playerName: frame.player?.name ?? null,
    value: formatEventValue(frame),
    clock: secondsToClock(frame.seconds),
    periodName: frame.period?.name ?? null,
    confidencePct: confidencePct(frame.confidence),
    receivedAt: frame.receivedAt,
    collapseKey: frame.incidentKey || frame.streamId || '',
  };
}

/** Newest-first by `receivedAt` (ISO). Ties keep input order (stable). */
function compareReceivedDesc(a: MatchEventFrame, b: MatchEventFrame): number {
  return new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime();
}

/**
 * Collapse a list of frames down to ONE per `incidentKey`, keeping the
 * highest-confidence frame for each key (ties broken by the later
 * `receivedAt` — the freshest snapshot of that incident). Frames with an
 * empty `incidentKey` are never collapsed (each is treated as unique via
 * its `streamId`). The result is sorted newest-first by `receivedAt`.
 */
export function collapseByIncidentKey(
  frames: MatchEventFrame[],
): MatchEventFrame[] {
  const best = new Map<string, MatchEventFrame>();
  const uncollapsed: MatchEventFrame[] = [];

  for (const frame of frames) {
    const key = frame.incidentKey;
    if (!key) {
      uncollapsed.push(frame);
      continue;
    }
    const prev = best.get(key);
    if (
      !prev ||
      frame.confidence > prev.confidence ||
      (frame.confidence === prev.confidence &&
        new Date(frame.receivedAt).getTime() >=
          new Date(prev.receivedAt).getTime())
    ) {
      best.set(key, frame);
    }
  }

  return [...best.values(), ...uncollapsed].sort(compareReceivedDesc);
}

/**
 * Apply the tab's filter to the raw frame buffer and produce the ordered
 * list of display rows.
 *   1. category filter (empty selection ⇒ show nothing),
 *   2. min-confidence gate,
 *   3. collapse-by-incidentKey unless `showAll`,
 *   4. newest-first order + row view models.
 */
export function buildMatchEventRows(
  frames: MatchEventFrame[],
  filter: Pick<MatchEventsFilter, 'categories' | 'minConfidence' | 'showAll'>,
): MatchEventRow[] {
  const selected = new Set(filter.categories);
  const filtered = frames.filter(
    (f) => selected.has(f.category) && f.confidence >= filter.minConfidence,
  );
  const collapsed = filter.showAll
    ? [...filtered].sort(compareReceivedDesc)
    : collapseByIncidentKey(filtered);
  return collapsed.map(frameToRow);
}

// ─── URL-param persistence (mirrors NotificationsPage filter-in-URL) ────────
//
// Prefixed `ev*` keys so the match-events filter never collides with the
// notifications filter keys sharing the same query string.

export function readMatchEventsFilterFromParams(
  params: URLSearchParams,
): MatchEventsFilter {
  const cats = params.getAll('evCat') as MatchEventCategory[];
  const confRaw = Number(params.get('evConf'));
  const fixtureRaw = params.get('evFixture');
  const fixtureId = fixtureRaw != null && fixtureRaw !== '' ? Number(fixtureRaw) : NaN;
  return {
    categories: cats.length > 0 ? cats : [...DEFAULT_MATCH_EVENT_CATEGORIES],
    minConfidence:
      Number.isFinite(confRaw) && confRaw >= 0 && confRaw <= 1 ? confRaw : 0,
    showAll: params.get('evAll') === '1',
    raw: params.get('evRaw') === '1',
    fixtureId: Number.isFinite(fixtureId) ? fixtureId : null,
  };
}

/**
 * Serialise ONLY the `ev*` keys for the match-events filter into the
 * given (mutated) params object. Every `ev*` key is cleared first so a
 * de-selection is reflected, and non-`ev*` keys (e.g. `tab`) are left
 * untouched so the caller can merge into the live search params.
 */
export function writeMatchEventsFilterToParams(
  filter: MatchEventsFilter,
  params: URLSearchParams,
): URLSearchParams {
  for (const key of ['evCat', 'evConf', 'evAll', 'evRaw', 'evFixture']) {
    params.delete(key);
  }
  for (const c of filter.categories) params.append('evCat', c);
  if (filter.minConfidence > 0)
    params.set('evConf', String(filter.minConfidence));
  if (filter.showAll) params.set('evAll', '1');
  if (filter.raw) params.set('evRaw', '1');
  if (filter.fixtureId != null)
    params.set('evFixture', String(filter.fixtureId));
  return params;
}
